import { useState, Fragment } from 'react';

import reduce from 'lodash/reduce';
import get from 'lodash/get';
import Ajv from 'ajv';

import { CardHeader, Divider, Grid, TextField, Typography, useMediaQuery, useTheme } from '@mui/material';

import FilterData from './filterData';
import FormModal from './formModal';
import MetaTagsTable from './metaTagsTable';

import Styles from '../theme/commonCss/Styles';
import { useOutletContext } from 'react-router-dom';
import OntologySelector from './OntologySelector';

const getFormDetails = {
  factory: {
    title: 'Factory',
    tag: 'factory_tags',
  },
  useCase: {
    title: 'Use Case',
    tag: 'use_case_tags',
  },
  digitalTwin: {
    title: 'digital twin',
    tag: 'use_case_tags',
  }
};

const FactoryStyles = {
  input: {
    '&.MuiTextField-root': {
      margin: '0px',
    },
  },
  errorMessage: {
    '&.MuiGrid-item': {
      padding: '0px 14px 12px 10px',
    },
  },
  gridContainer: { marginTop: '0px', marginBottom: '32px' },
  selectGridContainer: { marginTop: '12px', pb: '12px' },
  editTextFieldWrapper: { '&.MuiGrid-root.MuiGrid-item': { pl: 0, paddingBlock: '12px' } },
  editInputLabel: { paddingTop: '12px' },
  editTextField: { marginBlock: 0 },
};

const FactoryUseCaseFormModal = ({
  data,
  setData,
  tags,
  setTags,
  isModalOpen,
  title,
  isEdit,
  closeModal = () => { },
  onSave = () => { },
  ontologyData,
  metaDataSchema,
  dataSchema,
  uiSchema,
  isFrom,
  editOntoData,
  pageData,
  factoryLabel = ''
}: any) => {
  const themeParams: any = useTheme();
  const ajv = new Ajv();
  const [ajvErrors, setAjvErrors] = useState<any>({});
  const formDetails = get(getFormDetails, isFrom, {});
  const tags_key = get(formDetails, 'tag', '');
  const responsiveSize = useMediaQuery('(max-width:900px)');

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    setData({ ...data, [name]: value });
  };

  const handleSubmit = (event: any) => {
    event.preventDefault();

    let ajvSchema: any = { ...dataSchema };

    if (tags.length) {
      ajvSchema = {
        ...dataSchema,
        properties: {
          ...dataSchema.properties,
          [tags_key]: {
            type: 'array',
            items: tags.map((item: any) => metaDataSchema),
          },
        },
        required: [...dataSchema.required, tags_key],
      };
    }
    const validate = ajv.compile(ajvSchema);

    const trimmedData = reduce(
      data,
      (acc: any, value: any, key: any) => {
        acc[key] = typeof value === 'string' ? value.trim() : value;
        return {
          ...acc,
        };
      },
      {}
    );
    setData(trimmedData);

    const trimmedTags = tags.map((item: any) => {
      return {
        ...item,
        key: typeof item.key === 'string' ? item.key.trim() : item.key,
        value: typeof item.value === 'string' ? item.value.trim() : item.value,
      };
    });

    setTags(trimmedTags);

    const reqParams = {
      ...trimmedData,
      [tags_key]: trimmedTags,
    };

    if (validate({ ...reqParams })) {
      onSave(reqParams);
      closeModal();
      setAjvErrors({});
    } else {
      if (validate.errors) {
        setAjvErrors(validate.errors[0]);
      }
    }
  };

  const getOntoSelectedValue = (data: any) => {
    setData((remaining: any) => ({ ...remaining, ...data, onto_manage_id: data.onto_manage_id }));
  };

  return (
    <FormModal title={title} isOpen={isModalOpen} handleSubmit={handleSubmit} handleClose={closeModal}>
      <CardHeader
        title={isEdit ? 'Select Ontology Location' : `Select Ontology Location to add new ${factoryLabel.toLowerCase()}`}
        titleTypographyProps={Styles.title}
      />
      <Divider />
      <Grid container spacing={2} sx={FactoryStyles.gridContainer}>
        {uiSchema.map(({ label, name, type, required }: any, index: any) => {
          if (type === 'input') {
            return (
              <Fragment key={index + "_main"}>
                <Grid
                  key={index + "_grid"}
                  item={true}
                  xs={12}
                  sm={12}
                  md={4}
                  sx={{
                    ...Styles.gridItem,
                    '&.MuiGrid-item': {
                      paddingTop: responsiveSize ? '24px' : '32px',
                      justifyContent: responsiveSize ? 'flex-start' : 'flex-end',
                    },
                  }}
                >
                  <Typography sx={{ ...Styles.inputLabel, color: themeParams.palette.text.primary }} className={required ? 'required-field' : ''}>
                    {label.replace('Factory', factoryLabel)}
                  </Typography>
                </Grid>
                <Grid
                  key={index + "_grid_2"}
                  item={true}
                  xs={12}
                  sm={12}
                  md={8}
                  sx={{
                    '&.MuiGrid-item': {
                      pt: '0px',
                      pl: responsiveSize ? '16px' : '0px',
                    },
                  }}
                >
                  <TextField
                    key={index + "_text_field"}
                    sx={{
                      ...FactoryStyles.input,
                      pt: responsiveSize ? '4px' : '24px'
                    }}

                    margin='normal'
                    fullWidth
                    id={name}
                    onChange={handleChange}
                    name={name}
                    autoComplete={name}
                    autoFocus
                    variant='outlined'
                    value={data[name]}
                    size='small'
                    helperText={ajvErrors.instancePath && ajvErrors.instancePath.includes(name) && ajvErrors.message}
                    error={ajvErrors.instancePath && ajvErrors.instancePath.includes(name)}
                  />
                </Grid>
              </Fragment>
            );
          } else if (type === 'select') {
            return (
              <Grid
                key={index + "_select_grid"}
                container
                spacing={1}
                sx={{
                  ...FactoryStyles.selectGridContainer,
                  borderBottom: `1px solid ${themeParams.palette.customBorderColor.main}`, marginLeft: responsiveSize ? '16px' : '0px'
                }}
              >
                {isEdit ?

                  <OntologySelector
                    ontoManageId={data.onto_manage_id}
                    ontologyData={ontologyData}
                    showLayout={false}
                    onOntoFilterChange={getOntoSelectedValue}
                    isError={ajvErrors.instancePath && ajvErrors.instancePath.includes(name)}
                  />                 
                  : (
                    <FilterData
                      ontologyData={ontologyData}
                      showLayout={false}
                      onOntoFilterChange={getOntoSelectedValue}
                      isError={ajvErrors.instancePath && ajvErrors.instancePath.includes(name)}
                    />
                  )}
              </Grid>
            );
          } else {
            return <></>;
          }
        })}
      </Grid>
      {(isFrom !== 'digitalTwin') && <> <CardHeader
        title='Metadata (Supporting information for effective search)'
        titleTypographyProps={Styles.title}
      />
        <Divider />
        <MetaTagsTable
          data={tags}
          setData={setTags}
          error={ajvErrors.instancePath && ajvErrors.instancePath.includes(tags_key)}
        /> </>}
    </FormModal>
  );
};

export default FactoryUseCaseFormModal;
